import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Mail
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const FAQ = () => {
  const navigate = useNavigate();
  const [showMoreQuestions, setShowMoreQuestions] = useState(false);

  const topQuestions = [
    {
      question: "What am I buying?",
      answer: "A Ready-Made Brand Package — a cohesive foundation so you can start building without the blank page."
    },
    {
      question: "Is the domain included?",
      answer: "Yes. The domain is included. You'll claim it after purchase in Namecheap."
    },
    {
      question: "Do I own the domain after purchase?",
      answer: "Yes. Once you accept the Namecheap ownership request, the domain becomes yours."
    },
    {
      question: "What happens right after I purchase?",
      answer: "Three steps: Claim Domain → Connect to Emergent → Go live. Clear guidance at each step."
    },
    {
      question: "Do I need technical skills?",
      answer: "No. It's simple copy/paste setup. If you get stuck, we'll point out exactly what to do."
    }
  ];

  const moreQuestions = [
    {
      question: "Do you need my Namecheap password?",
      answer: "No. We never ask for passwords — you confirm ownership inside Namecheap."
    },
    {
      question: "How long does it take to go live?",
      answer: "Often the same day. Sometimes DNS takes a few hours to settle."
    },
    {
      question: "Can I change the brand name or domain?",
      answer: "Ready-Made Brands come as-is. Custom naming fits the Build Your Brand option."
    },
    {
      question: "Do you offer refunds?",
      answer: "Digital access is instant, so purchases are typically non-refundable. Delivery issues are always fixed quickly."
    },
    {
      question: "Will you release more brands?",
      answer: "Yes. New packages are added regularly."
    }
  ];

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Navigation */}
      <nav className="nav-glass sticky top-0 z-50 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center cursor-pointer" onClick={() => navigate("/")}>
            <span className="text-xl font-semibold text-[#2C332E]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Bloomwright Studio
            </span>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-[#78716C] hover:text-[#2C332E]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </nav>

      <main className="max-w-2xl mx-auto px-6 py-12 md:py-16">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-2xl md:text-3xl font-semibold text-[#2C332E] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            Questions, gently answered
          </h1>
          <p className="text-[#78716C]">
            Calm clarity, in one place.
          </p>
        </div>

        {/* Intro Copy */}
        <p className="text-sm text-[#78716C] text-center mb-10">
          If you're mid-setup, you can return anytime. Nothing will be lost.
        </p>

        {/* Section 1: Top Questions */}
        <div className="mb-10">
          <h2 className="text-sm font-medium text-[#78716C] uppercase tracking-wide mb-4">
            Top questions
          </h2>
          
          <Accordion type="single" collapsible className="space-y-3">
            {topQuestions.map((item, index) => (
              <AccordionItem 
                key={index} 
                value={`top-${index}`}
                className="bg-white rounded-lg border border-[#E6E1DB] overflow-hidden"
                data-testid={`faq-top-${index}`}
              >
                <AccordionTrigger className="px-5 py-4 text-left hover:no-underline hover:bg-[#F5F0EB]/50 transition-colors">
                  <span className="text-[#2C332E] font-medium text-sm pr-4">{item.question}</span>
                </AccordionTrigger>
                <AccordionContent className="px-5 pb-4">
                  <p className="text-sm text-[#78716C] leading-relaxed">{item.answer}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Section 2: More Questions (Collapsed) */}
        <div className="mb-10">
          <button
            onClick={() => setShowMoreQuestions(!showMoreQuestions)}
            className="flex items-center justify-between w-full text-left py-3 border-b border-[#E6E1DB]"
          >
            <h2 className="text-sm font-medium text-[#78716C] uppercase tracking-wide">
              More questions
            </h2>
            {showMoreQuestions ? (
              <ChevronUp className="w-4 h-4 text-[#78716C]" />
            ) : (
              <ChevronDown className="w-4 h-4 text-[#78716C]" />
            )}
          </button>
          
          {showMoreQuestions && (
            <Accordion type="single" collapsible className="space-y-3 mt-4">
              {moreQuestions.map((item, index) => (
                <AccordionItem 
                  key={index} 
                  value={`more-${index}`}
                  className="bg-white rounded-lg border border-[#E6E1DB] overflow-hidden"
                  data-testid={`faq-more-${index}`}
                >
                  <AccordionTrigger className="px-5 py-4 text-left hover:no-underline hover:bg-[#F5F0EB]/50 transition-colors">
                    <span className="text-[#2C332E] font-medium text-sm pr-4">{item.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-4">
                    <p className="text-sm text-[#78716C] leading-relaxed">{item.answer}</p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>

        {/* Still have questions */}
        <div className="text-center pt-6 border-t border-[#E6E1DB]">
          <p className="text-sm text-[#78716C] mb-4">
            Still have questions?
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open('mailto:support@bloomwrightstudio.com?subject=Question about Bloomwright Studio', '_blank')}
            className="rounded-full"
          >
            <Mail className="w-4 h-4 mr-2" />
            Get in touch
          </Button>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-[#E6E1DB]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-xs text-[#78716C]">
            © 2025 Bloomwright Studio
          </p>
        </div>
      </footer>
    </div>
  );
};

export default FAQ;
